import { useState } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useDevice } from '../../../store/devices/useDevices.ts';
import { useCapabilityValue } from '../../../store/devices/useCapabilityValue';

import { theme } from '../../../theme/theme';

import { Icon } from '../../common/Icon';
import { HomeyIcon } from '../../common/HomeyIcon';
import { CapabilitySelect } from '../CapabilitySelect';
import * as Containers from '../Containers';

import { getToggleImages } from './getToggleImages';
import { scrollbar } from '../../../theme/elements/scrollbars';

import { iconToggleSelected } from '../../../theme/icons/capabilities/toggle/selected';
import { iconToggleDefault } from '../../../theme/icons/capabilities/toggle/default';

import { variants, childVariants } from '../button/Button';

export function Toggle(props) {
  const { i18n } = useI18n();

  const { device } = useDevice({ deviceId: props.deviceId });

  const [selectedCapabilityId, setSelectedCapabilityId] = useState(() => {
    return props.component.capabilities[0];
  });

  const [value, setValue, capabilityObj] = useCapabilityValue({
    deviceId: props.deviceId,
    capabilityId: selectedCapabilityId,
  });

  if (capabilityObj == null) {
    return null;
  }

  const { urlTrue, urlFalse, useIconObj } = getToggleImages(capabilityObj.id, capabilityObj);

  function renderContent() {
    if (capabilityObj.id === 'onoff' && capabilityObj.getable === false) {
      return (
        <Toggle.GroupContainer>
          <Toggle.Wrapper>
            <Toggle.Base
              initial="initial"
              whileTap="whileTap"
              transition={{
                duration: 0.2,
              }}
              variants={variants}
              onClick={() => {
                setValue(true).catch(() => {});
              }}
            >
              <Icon
                as={motion.span}
                size="48px"
                transition={{
                  duration: 0.2,
                }}
                variants={childVariants}
                url={iconToggleSelected}
              />
            </Toggle.Base>

            <Toggle.Title>{i18n.messageFormatter('common.on')}</Toggle.Title>
          </Toggle.Wrapper>

          <Toggle.Wrapper>
            <Toggle.Base
              initial="initial"
              whileTap="whileTap"
              transition={{
                duration: 0.2,
              }}
              variants={variants}
              onClick={() => {
                setValue(false).catch(() => {});
              }}
            >
              <Icon
                as={motion.span}
                size="48px"
                transition={{
                  duration: 0.2,
                }}
                variants={childVariants}
                url={iconToggleDefault}
              />
            </Toggle.Base>

            <Toggle.Title>{i18n.messageFormatter('common.off')}</Toggle.Title>
          </Toggle.Wrapper>
        </Toggle.GroupContainer>
      );
    }

    return (
      <Toggle.Base
        initial={false}
        animate={{
          backgroundColor: value ? theme.color.white_o_100 : theme.color.black_o_50,
        }}
        onClick={() => {
          if (capabilityObj.getable === true) {
            const nextValue = !value;
            setValue(nextValue).catch(() => {});
          } else {
            setValue(true).catch(() => {});
          }
        }}
      >
        {useIconObj ? (
          <HomeyIcon
            iconObj={capabilityObj.iconObj}
            as={motion.span}
            size="48px"
            initial={false}
            animate={{
              backgroundColor: value ? theme.color.green : theme.color.white,
            }}
          />
        ) : (
          <Icon
            as={motion.span}
            size="48px"
            initial={false}
            animate={{
              backgroundColor: value ? theme.color.green : theme.color.white,
            }}
            url={value ? urlTrue : urlFalse}
          />
        )}
      </Toggle.Base>
    );
  }

  return (
    <Containers.Control>
      <Containers.Select>
        {props.component.capabilities.length > 1 && (
          <CapabilitySelect
            selectedCapabilityId={selectedCapabilityId}
            componentCapabilities={props.component.capabilities}
            capabilitiesObj={device?.capabilitiesObj}
            onSelectionChange={setSelectedCapabilityId}
          />
        )}
      </Containers.Select>
      <Containers.Action>{renderContent()}</Containers.Action>
    </Containers.Control>
  );
}

Toggle.Base = styled(motion.button)`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 180px;
  height: 180px;
  border: 0;
  border-radius: 50%;
  outline: none;
  cursor: pointer;
`;

Toggle.Wrapper = styled.div`
  display: flex;
  flex: 0 0 50%;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`;
Toggle.Title = styled.div`
  color: ${theme.color.text_invert_o_50};
  line-height: 16px;
  text-align: center;
  padding: 0 10px;
`;

Toggle.GroupContainer = styled.div`
  ${scrollbar};
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  padding: 0 68px;
  overflow-y: auto;

  ${Toggle.Base} {
    margin: 10px;
    height: 100px;
    width: 100px;
  }
`;
